import type { Art, Technique, TechniqueType } from '../types';
import { TechniqueType as TT } from '../types';

// A "series" is a themed collection of related techniques — e.g. a Kimura
// series or a Closed Guard series. Series don't OWN techniques; they reference
// the live technique pool by rule (category and/or name match), so the same
// technique can appear in several series (a kimura from closed guard shows up
// in both the Kimura and Closed Guard series). That overlap is intentional.
export interface Series {
  id: string;
  title: string;
  blurb?: string;
  /** Match by category id (see data/categories.ts). */
  categoryId?: string;
  /** Match by technique type (see TechniqueType). */
  techniqueType?: TechniqueType;
  /** Match if the technique name contains any of these (case-insensitive). */
  nameIncludes?: string[];
  /** Arts this series applies to. Undefined = BJJ-only (all current series). */
  arts?: Art[];
}

// Curated sample series. Order within the Journey list is the order shown.
export const series: Series[] = [
  { id: 'kimura', title: 'Kimura Series', blurb: 'The double-wristlock from every position.', nameIncludes: ['kimura'] },
  { id: 'closed-guard', title: 'Closed Guard Series', blurb: 'Attacks and sweeps from the full guard.', categoryId: 'closed-guard' },
  { id: 'triangle', title: 'Triangle Series', blurb: 'Strangles with the legs.', nameIncludes: ['triangle'] },
  { id: 'armbar', title: 'Armbar Series', blurb: 'Straight-arm locks, chained.', nameIncludes: ['armbar', 'arm bar', 'juji'] },
  { id: 'back-attacks', title: 'Back Attacks', blurb: 'Finishing from the back.', categoryId: 'back-control' },
  { id: 'guard-passing', title: 'Guard Passing', blurb: 'Getting past the legs.', techniqueType: TT.Pass },
];

/**
 * Resolve a series to the matching techniques from the live pool, ordered by
 * belt tier then name so each series reads beginner → advanced.
 */
export function resolveSeries(s: Series, techniques: Technique[]): Technique[] {
  const terms = s.nameIncludes?.map((t) => t.toLowerCase());
  return techniques
    .filter((t) => {
      if (s.categoryId && t.categoryId === s.categoryId) return true;
      if (s.techniqueType && t.type === s.techniqueType) return true;
      if (terms) {
        const name = t.name.toLowerCase();
        if (terms.some((term) => name.includes(term))) return true;
      }
      return false;
    })
    .sort((a, b) => a.tier - b.tier || a.name.localeCompare(b.name));
}
